import type { IRequestShortcutConfig, IScope } from 'angular';

import type { EntityService, FragmentDescriptorRequest } from '../PandoraSolutionBuilderAgent/services/entity-service';
import type { MxApplicationService } from '../../App/services/mx-application-service';
import type { HostUrlHelper } from '../../App/services/host-url-helper';
import type { UserProfileService } from '../../App/services/user-profile-service';
import type { EventService } from '../../App/services/event-service';
import type { I18n } from '../../i18n/mx-internationalization';
import type { HttpService } from '../../App/services/http-service';
import type { ConfigService, ConfigSettings } from '../../App/services/mx-config-service';
import type { DialogOpenerService } from '../PandoraSolutionBuilderAgent/services/dialog-opener-service';
import type { NotificationService } from '../../App/services/mx-notification-service';
import type { DataQueryService } from '../PandoraSolutionBuilderAgent/services/data-query-service';
import type { SidePanelService } from '../Pandora/services/mx-side-panel-service';
import type { MxShellInfoPanelService } from '../Pandora/components/mx-shell-info-panel/mx-shell-info-panel-service';
import type { MxActionManagerService } from '../Pandora/services/mx-action-manager-service';
import type { MxNavigationService } from '../Navigation/mx-navigation-service';

import type { MxApplication } from './public-types/MxApplication';
import type { MxObjectPreprocessor } from './public-types/MxObjectPreprocessor';
import type { MxUserProfile } from './public-types/MxUserProfile';
import type { MxRelationItemsRequest, RelationItemsResponse } from './public-types/RelationItemsRequest';
import type { FragmentDescriptorGetRequest, FragmentDescriptor } from './public-types/FragmentDescriptorsRequest';
import type { DestroyHandler, EventHandler } from './public-types/EventHandler';
import type { QueryStringParamsType } from './public-types/Http';
import type { ListPreviewOptions, ObjectDetails } from './public-types/SidePanel';
import type DetailsView from '../Pandora/model/details-view';
import type View from '../Pandora/model/view';
import type { AuthBehaviorProviderService } from '../../Auth/mx-auth-behavior-provider-service';
import type { EventCodes } from '../../App/model/event-codes';
import type {
	IEmptyFilterExpressionRequest,
	IFilterExpression,
	IFilterExpressionPath,
	IStrictParam,
	IWhereExpressionRequest,
} from '../PandoraSolutionBuilderAgent/model/filterExpressionUtils';
import type { ThemeService } from '../../Theme/mx-theme.service';
import type { Theme } from './public-types/Theme';

export class MxPublicService {
	static $inject = [
		'mx.SolutionBuilderAgent.EntityService',
		'mx.shell.AppsService',
		'mx.shell.HostUrlHelper',
		'mx.shell.UserProfileService',
		'mx.SolutionBuilderAgent.DataQueryService',
		'mx.shell.EventService',
		'mx.internationalization',
		'mx.shell.Http',
		'mx.shell.Config',
		'mx.SolutionBuilderAgent.DialogOpenerService',
		'mx.shell.SidePanelService',
		'mx.shell.NotificationService',
		'mx.shell.InfoPanelService',
		'mx.shell.ActionManagerService',
		'mx.shell.AuthBehaviorProviderService',
		'mx.shell.NavigationService',
		'mx.app.ThemeService',
	];

	constructor(
		private readonly entityService: EntityService,
		private readonly appsService: MxApplicationService,
		private readonly hostUrlHelper: HostUrlHelper,
		private readonly userProfileService: UserProfileService,
		private readonly dataQueryService: ReturnType<typeof DataQueryService>,
		private readonly eventService: EventService,
		private readonly internationalization: I18n,
		private readonly httpService: HttpService,
		private readonly configService: ConfigService,
		private readonly dialogOpenerService: ReturnType<typeof DialogOpenerService>,
		private readonly sidePanelService: ReturnType<typeof SidePanelService>,
		private readonly notificationService: NotificationService,
		private readonly infoPanelService: MxShellInfoPanelService,
		private readonly actionManagerService: MxActionManagerService,
		private readonly authBehaviourProviderService: AuthBehaviorProviderService,
		private readonly navigationService: MxNavigationService,
		private readonly themeService: ThemeService
	) {}

	public registerObjectParameterProcessor(preprocessor: MxObjectPreprocessor): void {
		return this.entityService.registerObjectParameterProcessor(preprocessor);
	}

	public getCurrentApplication(): Promise<MxApplication> {
		return this.appsService.getCurrentApplication();
	}

	public getFullImageUrl(imageUrl?: string, baseFolder = ''): string {
		return this.hostUrlHelper.getFullImageUrl(imageUrl, baseFolder);
	}

	public getUserProfile(): Promise<MxUserProfile> {
		return this.userProfileService.get();
	}

	public navigate(navigationItemId: string): Promise<void> {
		return this.navigationService.setNavigationItemById(navigationItemId);
	}

	public readonly auth = {
		onBeforeSessionDestroy: (cb: () => void | Promise<void>) => this.authBehaviourProviderService.onBeforeSessionDestroy(cb),
		onSessionExpired: (cb: () => void | Promise<void>) => this.authBehaviourProviderService.onSessionExpired(cb),
	};

	private prepareFilterExpression(filter?: string, filterPath?: IFilterExpressionPath, filterId?: string, filterParams: IStrictParam[] = []): IFilterExpression {
		return {
			toRequest() {
				if (filterId) {
					return {
						FilterExpression: {
							Id: filterId,
							Params: filterParams,
						},
					};
				}
				if (filterPath) {
					return {
						FilterExpression: {
							Path: filterPath,
							Params: filterParams,
						},
					};
				} else if (filter) {
					const whereExpression: IWhereExpressionRequest = {
						WhereExpression: filter,
					};
					return whereExpression;
				} else {
					return {} as IEmptyFilterExpressionRequest;
				}
			},
		};
	}

	public readonly data = {
		getRelationItems: (request: MxRelationItemsRequest): Promise<RelationItemsResponse> => {
			const { whereExpression, filterPath, filterId, filterParams = [], ...req } = request;
			(req as Record<string, unknown>).filter = this.prepareFilterExpression(whereExpression, filterPath, filterId, filterParams);
			return this.dataQueryService.getRelationItems(request);
		},
		getFragmentDescriptors: (request: FragmentDescriptorGetRequest, searchText = ''): Promise<FragmentDescriptor[]> => {
			const { filter, filterPath, filterId, filterParams = [], ...others } = request;
			const req: FragmentDescriptorRequest = others;
			req.filter = this.prepareFilterExpression(filter, filterPath, filterId, filterParams);
			return this.entityService.getFragmentDescriptors(req, searchText);
		},
	};

	public readonly event = {
		subscribe: (eventCode: EventCodes, handler: EventHandler, scope: IScope, destroyHandler: DestroyHandler): (() => void) =>
			this.eventService.subscribeTo(eventCode, handler, scope, destroyHandler),

		subscribeGlobal: (eventCode: EventCodes, handler: EventHandler): (() => void) => this.eventService.subscribeGlobal(eventCode, handler),

		dispatch: (eventCode: EventCodes, parameters?: unknown): void => this.eventService.dispatchEvent(eventCode, parameters),
	};

	public readonly http = {
		get: <DataType>(url: string, queryStringParams?: QueryStringParamsType, requestParams: IRequestShortcutConfig = {}, skipNotification = false) =>
			this.httpService.get<DataType>(url, queryStringParams, requestParams, skipNotification),

		post: <DataType>(url: string, data?: unknown, requestParams?: IRequestShortcutConfig, queryStringParams?: QueryStringParamsType, skipNotification = false) =>
			this.httpService.post<DataType>(url, data, requestParams, queryStringParams, skipNotification),

		put: <DataType>(url: string, data?: unknown, requestParams?: IRequestShortcutConfig, queryStringParams?: QueryStringParamsType, skipNotification = false) =>
			this.httpService.put<DataType>(url, data, requestParams, queryStringParams, skipNotification),

		remove: <DataType>(url: string, data?: unknown, requestParams?: IRequestShortcutConfig, queryStringParams?: QueryStringParamsType, skipNotification = false) =>
			this.httpService.remove<DataType>(url, data, requestParams, queryStringParams, skipNotification),

		upload: <DataType>(url: string, file?: unknown, requestParams?: IRequestShortcutConfig, queryStringParams?: QueryStringParamsType, skipNotification = false) =>
			this.httpService.upload<DataType>(url, file, requestParams, queryStringParams, skipNotification),
	};

	public readonly i18n = {
		get: (key: string): string => this.internationalization.get(key),
		getFormatted: (key: string, ...params: string[]): string => this.internationalization.getFormatted(key, ...params),
		getFormattedCurrency: (currency: { Value?: number | null; CC: string }, useSymbols = false, availableCurrencies: { code: string; symbol: string }[]): string =>
			this.internationalization.formatCurrency(currency, currency.CC, useSymbols, availableCurrencies),
	};

	public readonly config = {
		get: <Key extends keyof ConfigSettings>(key: Key): ConfigSettings[Key] => this.configService.get(key),
	};

	public readonly sidePanel = {
		getCurrentView: (): DetailsView | undefined => this.sidePanelService.getCurrentView(),
		setCurrentView: (view: DetailsView): void => this.sidePanelService.setCurrentView(view),
		closeCurrentView: (): void => this.sidePanelService.closeCurrentView(),
		closeView: (view: DetailsView): void => this.sidePanelService.closeView(view),
		refreshView: (view: DetailsView): void => this.sidePanelService.refreshView(view),
		getOpenedViews: (): DetailsView[] => this.sidePanelService.getOpenedViews(),
		openPreview: (objectDetails: ObjectDetails, openInFullScreen = false, parentView?: DetailsView): Promise<DetailsView> =>
			this.dialogOpenerService.openPreview(objectDetails, {}, openInFullScreen, parentView),
		openListPreview: (options: ListPreviewOptions, parentView: DetailsView): DetailsView => {
			const { filterExpression, filterPath, filterId, filterParams = [], ...req } = options;
			(req as Record<string, unknown>).filter = this.prepareFilterExpression(filterExpression, filterPath, filterId, filterParams);
			return this.dialogOpenerService.openObjectsListPreview(options, parentView);
		},
	};

	public readonly notification = {
		confirm: (title: string, prompt?: string, okLabel?: string, cancelLabel?: string): Promise<unknown> =>
			this.notificationService.confirm(title, prompt, okLabel, cancelLabel),
		confirmHtml: (title: string, html: string, okLabel?: string, cancelLabel?: string, extraLines?: string[]): Promise<unknown> =>
			this.notificationService.confirmHtml(title, html, okLabel, cancelLabel, extraLines),
		alert: (title: string, html: string, okLabel?: string, clickOutsideToClose?: boolean): Promise<unknown> =>
			this.notificationService.alert(title, html, okLabel, clickOutsideToClose),

		info: (message: string, title?: string): void => this.notificationService.info(message, title),
		warning: (message: string, title?: string): void => this.notificationService.warning(message, title),
		error: (message: string, title?: string): void => this.notificationService.error(message, title),
		success: (message: string, ariaMessage?: string): void => this.notificationService.success(message, ariaMessage),
	};

	public readonly infoPanel = {
		addWidget: (widget: View): boolean => this.infoPanelService.addWidget(widget),
		removeWidget: (widget: View): boolean => this.infoPanelService.removeWidget(widget),
	};

	public readonly action = {
		get: (id: string) => this.actionManagerService.getActionById(id),
	};

	public readonly theme = {
		getCurrent: (): Promise<Theme> => this.themeService.getCurrentTheme(),
	};
}
